/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {
  useCallback,
  useState
} from 'react';
import isEqual from 'lodash.isequal';
import {GenericObject} from '@ion/core/src/types/entities';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import {getJobNameTimestamp} from '../helpers';
import {VBusStatusProps} from './useVBusStatus';
import {VBusAction} from './VBusStatus';
import {
  VBusDetailsFields,
  VBusStatusAction
} from './VBusStatusDetailsForm';

export interface UseVBusStatusActionResult extends VBusStatusProps {
  setFormState?: (value: FormState) => void;
  formState?: GenericObject;
  actionEntry?: GenericObject;
  updateAction: (name: string, value: string) => void;
  getTaskName?: (action: string) => string;
  getName?: (action: string) => string;
  setShowSnackBar?: (showSnackBar: boolean) => void;
  showSnackBar?: boolean;
}

enum VBusOBDControl {
  ON = 'ObdControl ON',
  OFF = 'ObdControl OFF',
  VBUS = 'vbus'
}

/**
 * Re-usable hook for managing the actions of VBUS Status container.
 */
export const useVBusStatusAction = ({setTaskNickNameAction}): UseVBusStatusActionResult => {
  const action = {
    id     : 1,
    action : {
      status : ''
    }
  };
  const [formState, setFormState]       = useFormState();
  const [actionEntry, setActionEntry]   = useState<GenericObject>(action);
  const [showSnackBar, setShowSnackBar] = useState<boolean>(false);

  const getTaskName = useCallback((action) => {
    const on  = VBusOBDControl.ON;
    const off = VBusOBDControl.OFF;
    return isEqual(action, VBusAction.ON) ? on : off;
  }, []);

  const getName = useCallback((action) => {
    const on  = VBusAction.ON;
    const off = VBusAction.OFF;
    return `${isEqual(action, on) ? on : off} ${VBusOBDControl.VBUS} ${getJobNameTimestamp()}`;
  }, []);

  const getStatusAction = useCallback((action) => {
    const on  = VBusStatusAction.ON;
    const off = VBusStatusAction.OFF;
    return isEqual(action, VBusAction.ON) ? on : off;
  }, []);

  const updateAction = useCallback((name: string, value: string): void => {
    const updatedAction        = {...actionEntry};
    updatedAction.action[name] = value;
    setActionEntry(updatedAction);
    handleFormState(updatedAction?.action?.action);
    setTaskNickNameAction(updatedAction?.action?.action);
    setShowSnackBar(false);
  }, [actionEntry]);

  const handleFormState = useCallback((action): void => {
    setFormState({
      [VBusDetailsFields.ACTION] : {
        name  : VBusDetailsFields.ACTION,
        value : getStatusAction(action)
      }
    });
  }, []);

  return {
    setFormState,
    formState,
    actionEntry,
    updateAction,
    getTaskName,
    getName,
    setShowSnackBar,
    showSnackBar
  };
};
